
/* eslint-disable no-unused-vars */
import { debounce } from 'lodash';
import { normalizeSearchInput } from '@gid/vue-common/utils';

import { Vue, Component, Prop, Watch, Ref } from 'vue-property-decorator';
import DatePicker from '@gid/vue-common/components/DatePicker.vue';
import CrudListPagination from '@gid/vue-common/components/crud-list/CrudListPagination.vue';
import CrudListTable from '@gid/vue-common/components/crud-list/CrudListTable.vue';
import CrudListErrorMessage from '@gid/vue-common/components/crud-list/CrudListErrorMessage.vue';
import CrudDeleteButton from '@gid/vue-common/components/crud-list/CrudDeleteButton.vue';
import ImporterModal, {
  CurrentScreenUrl,
} from '../../components/importer/ImporterModal.vue';
import ReconcilePayment from './ReconcilePayment.vue';
import { AccountingDocumentStatusEnum } from '@gid/models';
import {
  PaymentRecordsModule,
  PaymentRecordsActionsEnum,
} from '../../store/accounting-payments.module';
import {
  CreateQueryParams,
  SConditionAND,
  SFields,
} from '@dataui/crud-request';
import { getModule } from 'vuex-module-decorators';
import * as ibantools from 'ibantools';

interface TargetStatusSelection {
  key: string;
  value: AccountingDocumentStatusEnum;
}

@Component({
  components: {
    DatePicker,
    CrudListPagination,
    CrudListTable,
    CrudListErrorMessage,
    CrudDeleteButton,
    ImporterModal,
    ReconcilePayment,
  },
})
export default class PaymentRecordsView extends Vue {
  @Prop() paymentId?: string;
  @Ref('importModal') readonly importModal;
  @Ref('reconcilePaymentModal') readonly reconcilePaymentModal;

  inProgress: boolean = false;
  currentScreenUrl: CurrentScreenUrl = {
    entityPath: 'payments',
    query: '',
    queryString: '',
    noPagingQuery: '',
    noPagingQueryString: '',
  };

  importUrl(format, currentScreenUrl: CurrentScreenUrl) {
    return '/documents-api/accounting/payments/import/csv';
  }

  created() {
    this.refreshDocumentList();
  }

  // Computed
  get store(): PaymentRecordsModule {
    return getModule(PaymentRecordsModule, this.$store);
  }
  get columns() {
    return [
      {
        key: 'date',
        sortable: true,
        label: 'Date', // this.$t('accounting.document.issue-date'),
      },
      {
        key: 'accountGid',
        sortable: true,
        label: 'Account', //this.$t('accounting.common.document-number'),
      },
      {
        key: 'reference',
        sortable: true,
        label: 'Reference',
      },
      {
        key: 'amount',
        sortable: true,
        label: 'Amount', //this.$t('accounting.common.amount'),
      },
      {
        key: 'nameOther',
        sortable: true,
        label: 'Name', //this.$t('accounting.common.target-name'),
      },
      {
        key: 'accountOther',
        sortable: true,
        label: 'Payer/Payee account', //this.$t('accounting.common.total-amount'),
      },
      {
        key: 'actions',
        label: 'Actions',
        class: 'text-right text-nowrap',
      },
    ];
  }

  @Watch('store.filters', {
    deep: true,
  })
  searchChange(value) {
    this.throttledFind();
  }

  buildSearch() {
    const search: Array<SFields | SConditionAND> = [];
    if (this.paymentId) {
      search.push({
        id: {
          $eq: this.paymentId,
        },
      });
      return { search: { $and: search } };
    }

    if (this.store.filters.dateFrom) {
      search.push({
        date: {
          $gte: this.store.filters.dateFrom,
        },
      });
    }
    if (this.store.filters.dateTo) {
      search.push({
        date: {
          $lte: this.store.filters.dateTo,
        },
      });
    }

    if (this.store.filters.search) {
      const normalizedSearch = normalizeSearchInput(this.store.filters.search);
      const searchIban = ibantools.isValidIBAN(normalizedSearch)
        ? (ibantools.electronicFormatIBAN(normalizedSearch) as string)
        : normalizedSearch;
      search.push({
        $or: [
          {
            nameOther: {
              $contL: normalizedSearch,
            },
          },
          {
            accountOther: {
              $contL: searchIban,
            },
          },
          {
            referenceText: {
              $contL: normalizedSearch,
            },
          },
          {
            referenceNumber: {
              $contL: normalizedSearch,
            },
          },
        ],
      });
    }

    if (this.store.filters.linkedDocuments === 'with') {
      search.push({
        'documents.id': {
          $notnull: this.store.filters.dateTo,
        },
      });
    }

    if (this.store.filters.linkedDocuments === 'without') {
      search.push({
        'documents.id': {
          $isnull: this.store.filters.dateTo,
        },
      });
    }

    return {
      search: {
        $and: search,
      },
    };
  }

  refreshDocumentList() {
    const { search } = this.buildSearch();

    const query: CreateQueryParams = {
      page: this.store.pagination.currentPage,
      limit: this.store.pagination.perPage,
    };
    this.store[PaymentRecordsActionsEnum.ITEMS_FETCH]({
      query,
      search,
    });
  }
  throttledFind = debounce(this.refreshDocumentList, 400);

  importFileModal() {
    this.importModal.open();
  }

  onImportError(event) {
    console.error(event);
    this.store.setLastError({
      error: event.data,
    });
  }

  link(payment) {
    this.reconcilePaymentModal.openForPayment(payment.id);
  }

  /**
   * Decide whether the popover row should show the cancellation side of the
   * doc rather than the original. Mirrors inferReconcileCancellationMap on
   * the documents-service so the popover and the reconcile modal agree.
   */
  private isCancellationSide(
    doc: {
      number: string;
      cancellationNumber?: string | null;
      status?: string;
      cancellationStatus?: string | null;
    },
    documentNumbers: string[] | null,
  ): boolean {
    if (!doc.cancellationNumber) return false;
    if ((documentNumbers || []).includes(doc.cancellationNumber)) return true;
    // An *active* cancellation must not flip an existing link off the
    // original side; only an already-settled cancellation (PAID, original
    // reverted to SENT) shows the cancellation side.
    return (
      doc.cancellationStatus === AccountingDocumentStatusEnum.PAID &&
      doc.status === AccountingDocumentStatusEnum.SENT
    );
  }

  documentDisplayNumber(
    doc: {
      number: string;
      cancellationNumber?: string | null;
      status?: string;
      cancellationStatus?: string | null;
    },
    documentNumbers: string[] | null,
  ): string {
    if (this.isCancellationSide(doc, documentNumbers)) {
      return doc.cancellationNumber as string;
    }
    return doc.number;
  }

  documentDisplayAmount(
    doc: {
      number: string;
      cancellationNumber?: string | null;
      status?: string;
      cancellationStatus?: string | null;
      totalAmount: number;
    },
    documentNumbers: string[] | null,
  ): number {
    // Cancellation context inverts the sign — a refund coming in for an
    // invoice is shown as negative, a reclaim against a credit_note as
    // positive — matching the reconcile modal's totals.
    if (this.isCancellationSide(doc, documentNumbers)) {
      return -doc.totalAmount;
    }
    return doc.totalAmount;
  }
}
